import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useAppDispatch } from "src/store";
import { DASHBOARD_DATE_RANGE_TYPES } from "src/constants";
import {
  selectDashboardById,
  selectDashboardDateRangesByTrackersCollectionId,
} from "src/store/selectors";
import {
  createDashboardDefaultDateRanges,
  fetchDashboardDateRangesByTrackersCollectionId,
} from "src/store/actions";

type Props = {
  trackersCollectionId: TrackersCollection.Data["id"];
};

export const useDashboardDateRangesLoader = ({
  trackersCollectionId,
}: Props) => {
  const dispatch = useAppDispatch();

  const dashboardDateRanges = useSelector((state: Store.RootState) =>
    selectDashboardDateRangesByTrackersCollectionId(
      state,
      trackersCollectionId,
    ),
  );

  const dashboard = useSelector((state: Store.RootState) =>
    selectDashboardById(state, trackersCollectionId),
  );

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  const isLoaded = useMemo<boolean>(
    () => Boolean(dashboardDateRanges.length),
    [dashboardDateRanges.length],
  );

  useEffect(() => {
    if (isLoaded) setLoadingStatus("succeeded");
    else setLoadingStatus("idle");
  }, [isLoaded]);

  useEffect(() => {
    if (!dashboard || loadingStatus !== "idle" || dashboardDateRanges.length)
      return;

    setLoadingStatus("loading");

    dispatch(
      fetchDashboardDateRangesByTrackersCollectionId(trackersCollectionId),
    )
      .unwrap()
      .then((dashboardDateRanges) => {
        const dashboardDateRangeTypesToCreate =
          getMissingDefaultDashboardDateRangeTypes(dashboardDateRanges);

        if (!dashboardDateRangeTypesToCreate.length)
          return setLoadingStatus("succeeded");

        dispatch(
          createDashboardDefaultDateRanges({
            trackersCollectionId,
            authorId: dashboard.authorId,
            companyId: dashboard.companyId,
            types: dashboardDateRangeTypesToCreate,
          }),
        )
          .unwrap()
          .then(() => setLoadingStatus("succeeded"));
      })
      .catch((error) => {
        console.error(error);

        setLoadingStatus("failed");
      });
  }, [
    dispatch,
    dashboard,
    loadingStatus,
    trackersCollectionId,
    dashboardDateRanges,
  ]);

  return { loadingStatus };
};

function getMissingDefaultDashboardDateRangeTypes(
  dashboardDateRanges: DashboardDateRange.Data[],
): DashboardDateRange.Type[] {
  const [missingTypes, createdTypes] = [
    new Set<DashboardDateRange.Type>(),
    new Set<DashboardDateRange.Type>(),
  ];

  for (const dashboardDateRange of dashboardDateRanges)
    if (dashboardDateRange.type) createdTypes.add(dashboardDateRange.type);

  for (const type of DASHBOARD_DATE_RANGE_TYPES)
    if (!createdTypes.has(type)) missingTypes.add(type);

  return Array.from(missingTypes);
}
